import DataSyncCgObjectsStore from '../../stores/object/DataSyncCgObjectsStore';
import { DataSyncObjectsStore } from '../../stores/object/DataSyncObjectsStore';
import {
  CGObjectDefinition,
  DataSyncItem,
  Destination,
  MappingMode,
  ObjectDefinition,
  OptimizelyProduct,
  Source
} from '../../types/DataSyncItem';
import { FieldMappingEntry } from '../../types/DataSyncViewModel';
import { FIELD_PATH_SEPARATOR, FieldDefinition, isObjectArray } from '../../types/FieldDefinition';
import { INCOMPLETE, ValidationType } from '../../types/FieldMapping';
import { DataSyncObjectDefinitionService } from './DataSyncObjectDefinitionService';
import { findRankedMatches } from '../../lib/fieldMatcher';
import { isOneToOneClonedMapping } from '../../lib/dataSync';
import { getInvalidSourceFields, getFieldMappingValidations } from '../../utils/fieldMappingValidator';

interface AutoMapCandidate {
  destinationPath: string;
  destinationField: FieldDefinition;
  sourcePath: string;
  score: number;
}

export class DataSyncMappingService {

  public static async determineMappingMode(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null
  ): Promise<MappingMode> {
    return await DataSyncMappingService.isCGDestinationInheritedFromSource(
      source, destination, sourceStore, destinationStore
    ) ? MappingMode.FIXED : MappingMode.EDITABLE;
  }

  public static async isCGDestinationInheritedFromSource(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null
  ): Promise<boolean> {
    const destinationObject = await destinationStore?.getDestinationObjectSchema(
      destination.object.object_name
    );

    const sourceObject = sourceStore?.getSourceObjectDefinition(source.object.object_name);

    if (
      destination.product !== OptimizelyProduct.CGR ||
      !destinationObject ||
      !sourceObject ||
      !destinationObject.cg_metadata
    ) {
      return false;
    }

    const isOCP = destinationObject.cg_metadata.created_by === 'OCP';
    const isMatchingProduct = destinationObject.cg_metadata.source_product === source.product.toString();
    const isMatchingSource = destinationObject.cg_metadata.source_object_name === sourceObject.object_name;

    return isOCP && isMatchingProduct && isMatchingSource;
  }

  public static async inheritSourceSchema(
    source: Source,
    destinationObjectName: string,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null,
    baseType?: string
  ): Promise<FieldMappingEntry[]> {
    if (!sourceStore || !destinationStore) {
      return [];
    }
    const dataSyncCgObjectStore = destinationStore as DataSyncCgObjectsStore;

    const sourceObject = sourceStore.getSourceObjectDefinition(source.object.object_name);
    if (!sourceObject) {
      return [];
    }

    const sourceFields = await sourceStore.getSourceObjectFields(source.object.object_name);
    if (sourceFields.length === 0) {
      return [];
    }

    // Fetch existing destination fields to preserve required (base contract) fields and determine base type.
    // Uses getDestinationObjectFields which fetches from API for CG destinations,
    // since getDestinationObjectDefinition returns objects with empty fields.
    const existingDestinationFields = await destinationStore.getDestinationObjectFields(destinationObjectName);

    const baseTypeDefinitions = dataSyncCgObjectStore.getBaseTypeDefinitions();

    // Use existing base type or determine it from fields if base contracts are available in cg instance
    const resolvedBaseType = baseType || (baseTypeDefinitions.length > 0
      ? DataSyncObjectDefinitionService.determineBaseTypeFromFields(existingDestinationFields || [])
      : undefined);

    const destinationObject = DataSyncObjectDefinitionService.convertToCGObject(
      source.product,
      sourceFields,
      source.object.object_name,
      destinationObjectName,
      dataSyncCgObjectStore?.getBaseTypeDefinitions() || [],
      resolvedBaseType
    );

    const existingRequiredFields = existingDestinationFields.filter((field) => field.required);

    // Add existing required fields to the transient object if they don't already exist
    existingRequiredFields.forEach((requiredField) => {
      const existingField = destinationObject.fields.find((field) => field.name === requiredField.name);
      if (!existingField) {
        destinationObject.fields.push({ ...requiredField });
      } else {
        existingField.required = true;
      }
    });

    destinationStore?.setTransientDestinationObject(destinationObject);

    // Initialize empty field mappings
    const fieldMappingEntries: FieldMappingEntry[] = [];

    // create mapping for mandatory fields
    for (const field of destinationObject.fields.filter((desField) => desField.required)) {
      fieldMappingEntries.push({
        sourceField: INCOMPLETE,
        destinationField: field.name,
        disabled: false
      });
    }

    // Filter source fields to exclude _modified and _deleted if source is CGR
    const filteredSourceFields = DataSyncObjectDefinitionService
      .filterSourceFields(sourceFields, source.product);

    for (const sourceField of filteredSourceFields) {
      this.createFieldMappings(source.product, resolvedBaseType, fieldMappingEntries, sourceField, sourceField.name);
    }
    return fieldMappingEntries;
  }

  public static async generateMappingsForInheritedSchema(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null,
    existingFieldMappings: FieldMappingEntry[]
  ): Promise<{
    mappings: FieldMappingEntry[];
    updatedFields: FieldDefinition[];
  }> {

    const destinationObjectName = destination.object.object_name;
    const destinationObjectFields = await destinationStore?.getDestinationObjectFields(
      destinationObjectName
    );

    if (!destinationObjectFields) {
      return { mappings: [], updatedFields: [] };
    }

    const baseTypeDefinitions = (destinationStore as DataSyncCgObjectsStore)?.getBaseTypeDefinitions() || [];

    // Use the config or determine the base type from the fields if baseContracts are availabe in cg instance
    const baseType = destination.config?.content_graph?.base_contract || (baseTypeDefinitions.length > 0
      ? DataSyncObjectDefinitionService.determineBaseTypeFromFields(destinationObjectFields || [])
      : undefined);

    const clonedFields = JSON.parse(JSON.stringify(destinationObjectFields)) as FieldDefinition[];

    const transientObjectDefinition = {
      object_name: destinationObjectName,
      base_type: baseType,
      source_product: source.product,
      source_object_name: source.object.object_name,
      created_by: 'OCP',
      fields: clonedFields
    } as CGObjectDefinition;

    const sourceObjectFields = await sourceStore?.getSourceObjectFields(
      source.object.object_name
    ) || [];

    let newFieldMappings: FieldMappingEntry[] = existingFieldMappings.map((m) => {
      if (baseType) {
        const topLevelDestField = m.destinationField.split(FIELD_PATH_SEPARATOR)[0];
        if (DataSyncObjectDefinitionService.isCGBaseContractField(OptimizelyProduct.CGR, topLevelDestField)) {
          return { ...m };
        }
      }
      return { ...m, isCloned: true };
    });

    // Filter source fields to exclude _modified and _deleted if source is CGR
    const filteredSourceFields = DataSyncObjectDefinitionService
      .filterSourceFields(sourceObjectFields, source.product);

    for (const field of filteredSourceFields) {
      // Handle nested fields recursively to create mappings for all source fields
      newFieldMappings = this.checkAndCreateMissingFieldMappings(
        source.product,
        field,
        field.name,
        newFieldMappings,
        transientObjectDefinition,
        filteredSourceFields
      );
    }

    destinationStore?.setTransientDestinationObject(transientObjectDefinition);
    return {
      mappings: newFieldMappings,
      updatedFields: transientObjectDefinition.fields,
    };
  }

  public static filterDisableFields(
    objectDefinition: ObjectDefinition,
    fieldMappings: FieldMappingEntry[]
  ): FieldDefinition[] {
    const disabledFieldPaths = new Set(fieldMappings.filter((m) => m.disabled).map((m) => m.destinationField));

    // Deep clone the fields to avoid mutating the original
    const filteredFields = JSON.parse(JSON.stringify(objectDefinition?.fields || [])) as FieldDefinition[];

    // Recursively remove disabled nested fields
    const filterNestedFields = (fields: FieldDefinition[], pathPrefix: string = ''): FieldDefinition[] => {
      return fields.filter((field) => {
        const currentPath = pathPrefix ? `${pathPrefix}${FIELD_PATH_SEPARATOR}${field.name}` : field.name;

        // If this field itself is disabled, remove it
        if (disabledFieldPaths.has(currentPath)) {
          return false;
        }

        // If this field has nested fields, filter them recursively
        if (field.object_schema?.fields) {
          field.object_schema.fields = filterNestedFields(field.object_schema?.fields, currentPath);
        }

        // If this field has array fields, filter them recursively
        if (field.array && isObjectArray(field.array)) {
          field.array.fields = filterNestedFields(field.array.fields, currentPath);
        }

        return true;
      });
    };

    return filterNestedFields(filteredFields);
  }

  /**
   * Auto-map: find best matching source fields for unmapped destination fields.
   * Pure function -- takes current state and returns updated mappings.
   */
  public static autoMap(
    destinationFields: FieldDefinition[],
    sourceFields: FieldDefinition[],
    existingMappings: FieldMappingEntry[]
  ): FieldMappingEntry[] {
    const collectLeafDestinations = (fields: FieldDefinition[], prefix = ''):
    Array<{field: FieldDefinition, path: string}> => {
      const leaves: Array<{field: FieldDefinition, path: string}> = [];

      for (const field of fields) {
        const fieldPath = prefix ? `${prefix}/${field.name}` : field.name;

        if (field.object_schema?.fields) {
          leaves.push(...collectLeafDestinations(field.object_schema.fields, fieldPath));
        } else if (field.array && field.array.type === 'object' && 'fields' in field.array) {
          leaves.push(...collectLeafDestinations(field.array.fields, fieldPath));
        } else {
          leaves.push({ field, path: fieldPath });
        }
      }

      return leaves;
    };

    const allLeafDestinations = collectLeafDestinations(destinationFields);

    let currentMappings = [...existingMappings];

    const unmappedDestinations = allLeafDestinations.filter((item) => {
      const existingMapping = currentMappings.find(
        (m) => m.destinationField === item.path
      );
      return !existingMapping || !existingMapping.sourceField;
    });

    if (unmappedDestinations.length === 0) {
      return currentMappings;
    }

    // Array-correspondence validation depends on the mappings made so far, so the cache is
    // dropped whenever an assignment lands.
    let invalidSourceCache = new Map<string, Set<string>>();
    const invalidSourcesFor = (destinationPath: string): Set<string> => {
      let cached = invalidSourceCache.get(destinationPath);
      if (!cached) {
        cached = new Set<string>(
          getInvalidSourceFields(destinationPath, sourceFields, destinationFields, currentMappings).keys()
        );
        invalidSourceCache.set(destinationPath, cached);
      }
      return cached;
    };

    // Pass 1: score every (destination, source) pair up front so the result does not depend
    // on the order destinations happen to appear in the schema.
    const candidates: AutoMapCandidate[] = [];
    for (const destItem of unmappedDestinations) {
      const matches = findRankedMatches(destItem.field, sourceFields, invalidSourcesFor(destItem.path));
      for (const match of matches) {
        candidates.push({
          destinationPath: destItem.path,
          destinationField: destItem.field,
          sourcePath: match.field.name,
          score: match.score,
        });
      }
    }

    // Best score wins; required destinations break ties so an optional field can never
    // out-compete a required one at equal confidence.
    candidates.sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.destinationField.required ?? false) - Number(a.destinationField.required ?? false) ||
        a.destinationPath.localeCompare(b.destinationPath)
    );

    // Pass 2: greedy assignment. A source claimed by this run is never reassigned, so a
    // lower-scoring destination cannot take a source away from a higher-scoring one.
    const claimedDestinations = new Set<string>();
    const claimedSources = new Set<string>();

    for (const candidate of candidates) {
      const { destinationPath, sourcePath } = candidate;

      if (claimedDestinations.has(destinationPath) || claimedSources.has(sourcePath)) {
        continue;
      }

      if (invalidSourcesFor(destinationPath).has(sourcePath)) {
        continue;
      }

      const validation = getFieldMappingValidations({
        source: sourcePath,
        destination: destinationPath,
        sourceFields,
        destinationFields,
      });

      if (validation && validation.type === ValidationType.ERROR) {
        continue;
      }

      const existingMappingWithSource = currentMappings.find(
        (m) => m.sourceField === sourcePath && m.destinationField !== destinationPath && !m.disabled
      );

      // Only a 1:1 clone yields its source. Anything else -- a manual mapping, a base contract
      // mapping, or one made earlier in this run -- is deliberate, so auto-map skips the
      // candidate and the destination falls through to its next-best source.
      if (existingMappingWithSource && !isOneToOneClonedMapping(existingMappingWithSource)) {
        continue;
      }

      claimedDestinations.add(destinationPath);
      claimedSources.add(sourcePath);

      if (existingMappingWithSource) {
        currentMappings = currentMappings.map((m) =>
          m.destinationField === existingMappingWithSource.destinationField
            ? { ...m, disabled: true }
            : m
        );
      }

      const existingDestMapping = currentMappings.find(
        (m) => m.destinationField === destinationPath
      );

      if (existingDestMapping) {
        currentMappings = currentMappings.map((m) =>
          m.destinationField === destinationPath
            ? { ...m, sourceField: sourcePath, disabled: false, isAutoMapped: true }
            : m
        );
      } else {
        currentMappings.push({
          destinationField: destinationPath,
          sourceField: sourcePath,
          disabled: false,
          isAutoMapped: true,
        });
      }

      invalidSourceCache = new Map<string, Set<string>>();
    }

    return currentMappings;
  }

  public static async determineInitialMappingMode(
    dataSync: DataSyncItem,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null): Promise<MappingMode> {
    const mappingMode = await DataSyncMappingService.determineMappingMode(
      dataSync.source, dataSync.destination, sourceStore, destinationStore
    );
    return mappingMode;
  }

  private static createFieldMappings(
    sourceProduct: OptimizelyProduct,
    baseType: string | undefined,
    fieldMappingEntries: FieldMappingEntry[],
    field: FieldDefinition,
    fieldPath: string,
    isInsideBaseContract: boolean = false
  ): void {
    const isBaseContract = isInsideBaseContract ||
      DataSyncObjectDefinitionService.isCGBaseContractField(sourceProduct, field.name);

    if (field.object_schema?.fields) {
      for (const nestedField of field.object_schema.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        this.createFieldMappings(
          sourceProduct, baseType, fieldMappingEntries, nestedField, nestedFieldPath, isBaseContract
        );
      }
    } else if (field.array && isObjectArray(field.array)) {
      for (const nestedField of field.array.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        this.createFieldMappings(
          sourceProduct, baseType, fieldMappingEntries, nestedField, nestedFieldPath, isBaseContract
        );
      }
    } else {
      // Skip base contract fields — they should not be auto-mapped (users map them manually)
      if (isBaseContract && !!baseType) {
        return;
      }

      if (!fieldMappingEntries.some((pair) => pair.destinationField === fieldPath)) {
        fieldMappingEntries.push({
          sourceField: fieldPath,
          destinationField: fieldPath,
          disabled: false,
          isCloned: true
        });
      }
    }
  }

  /**
   * Add a field to the correct nested level in the transient object definition
   * Traverses based on sourceFieldPath and creates necessary parent structure from sourceFields
   */
  private static addFieldToTransientObject(
    sourceFieldPath: string,
    sourceFields: FieldDefinition[],
    transientFields: FieldDefinition[]
  ): void {
    const pathParts = sourceFieldPath.split(FIELD_PATH_SEPARATOR);

    if (pathParts.length === 1) {
      // Top-level field - find in sourceFields and add if it doesn't exist in transientFields
      const sourceField = sourceFields.find((f) => f.name === sourceFieldPath);
      const fieldExists = transientFields.find((f) => f.name === sourceFieldPath);

      if (sourceField && !fieldExists) {
        transientFields.push({ ...sourceField });
      }
      return;
    }

    // For nested fields, traverse the path step by step
    const [currentFieldName, ...remainingPath] = pathParts;

    const currentSourceField = sourceFields.find((f) => f.name === currentFieldName);
    if (!currentSourceField) {
      return;
    }

    let currentTransientField = transientFields.find((f) => f.name === currentFieldName);
    if (!currentTransientField) {
      currentTransientField = { ...currentSourceField };
      transientFields.push(currentTransientField);
    }

    let nextSourceFields: FieldDefinition[];
    let nextTransientFields: FieldDefinition[];

    if (currentSourceField.object_schema?.fields) {
      if (!currentTransientField.object_schema) {
        currentTransientField.object_schema = { fields: [] };
      }
      nextSourceFields = currentSourceField.object_schema.fields;
      nextTransientFields = currentTransientField.object_schema.fields;
    } else if (currentSourceField.array && isObjectArray(currentSourceField.array)) {
      if (!currentTransientField.array || !isObjectArray(currentTransientField.array)) {
        currentTransientField.array = { type: 'object', fields: [] };
      }
      nextSourceFields = currentSourceField.array.fields;
      nextTransientFields = currentTransientField.array.fields;
    } else if (currentSourceField.array) {
      if (!currentTransientField.array) {
        currentTransientField.array = { ...currentSourceField.array };
      }
      return;
    } else {
      return;
    }

    // Recursively add to the nested structure
    this.addFieldToTransientObject(
      remainingPath.join(FIELD_PATH_SEPARATOR),
      nextSourceFields,
      nextTransientFields
    );
  }

  /**
   * Recursively check if source fields exist in mappings and create disabled mappings for missing ones
   */
  private static checkAndCreateMissingFieldMappings(
    sourceProduct: OptimizelyProduct,
    sourceField: FieldDefinition,
    fieldPath: string,
    fieldMappings: FieldMappingEntry[],
    transientObjectDefinition: CGObjectDefinition,
    allSourceFields: FieldDefinition[],
    isInsideBaseContract: boolean = false
  ): FieldMappingEntry[] {
    const isBaseContract = isInsideBaseContract ||
      DataSyncObjectDefinitionService.isCGBaseContractField(sourceProduct, sourceField.name);

    if (sourceField.object_schema?.fields) {
      for (const nestedField of sourceField.object_schema.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        fieldMappings = this.checkAndCreateMissingFieldMappings(
          sourceProduct,
          nestedField,
          nestedFieldPath,
          fieldMappings,
          transientObjectDefinition,
          allSourceFields,
          isBaseContract
        );
      }
    } else if (sourceField.array && isObjectArray(sourceField.array)) {
      for (const arrayField of sourceField.array.fields) {
        const arrayFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${arrayField.name}`;
        fieldMappings = this.checkAndCreateMissingFieldMappings(
          sourceProduct,
          arrayField,
          arrayFieldPath,
          fieldMappings,
          transientObjectDefinition,
          allSourceFields,
          isBaseContract
        );
      }
    } else {
      // Skip base contract fields — they are not auto-mapped
      if (isBaseContract) {
        return fieldMappings;
      }

      const existingMapping = fieldMappings.some(
        (m) => m.sourceField === fieldPath && m.destinationField === fieldPath
      );
      if (!existingMapping) {
        fieldMappings.push({
          sourceField: fieldPath,
          destinationField: fieldPath,
          disabled: true,
          isCloned: true,
        });
        this.addFieldToTransientObject(
          fieldPath,
          allSourceFields,
          transientObjectDefinition.fields
        );
      }
    }

    return fieldMappings;
  }
}
